/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { User as UserType, fetcher } from '@/data/_User';
import { DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC, DATA_KEY_USER } from '@/data/constants/dataKey';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { GENERIC_USER_ID } from '@/data/constants/user';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { guestIdentityLoginFetcher } from '@/data/Content/GuestFetcher';
import { personMutatorKeyMatcher } from '@/data/Content/Login';
import { useSettings } from '@/data/Settings';
import { getUser } from '@/data/User-Server';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { expand, shrink } from '@/data/utils/keyUtil';
import { useEffect, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
export { fetcher, getUser };
export type User = UserType;

export const useUser = () => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId, langId } = getClientSideCommon(settings, router);
	// server-side param has cookie, client does not. need to separate cookie and preview header.
	const params = useExtraRequestParameters();
	const { mutate } = useSWRConfig();
	const {
		data,
		mutate: mutateUser,
		error,
	} = useSWR(storeId ? [shrink({ storeId, langId }), DATA_KEY_USER] : null, async ([props]) =>
		fetcher(true)({ ...expand(props), params })
	);

	useEffect(() => {
		if (
			data?.userId &&
			data.userId !== GENERIC_USER_ID &&
			data.context?.basicInfo.registerType === 'G' &&
			data.context.isPartiallyAuthenticated
		) {
			guestIdentityLoginFetcher(true)(storeId, params).then(() => {
				mutate(personMutatorKeyMatcher(EMPTY_STRING)); // current page
				mutate(personMutatorKeyMatcher(DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC), undefined);
			});
		}
		// only need to run once
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		user: data,
		mutateUser,
		loading: !error && !data,
		error,
	};
};

export const useUserCheckLoggedIn = () => {
	const { user } = useUser();
	const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

	useEffect(() => {
		setIsLoggedIn(() => user?.isLoggedIn ?? false);
	}, [user]);

	return {
		user,
		isLoggedIn,
	};
};
